import type { DynamicPricingOffer, LetterOffersResponse } from "@/api/offers";

const toInt = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === "string" && value.trim()) {
    const n = Number.parseInt(value, 10);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

export const getOfferBadgeText = (offer: DynamicPricingOffer | null | undefined): string | null => {
  if (!offer) return null;
  const bonus = Math.max(0, toInt(offer.bonus_credits));
  const bundlePrice = Math.max(0, toInt(offer.bundle_price_cents));
  const price = Math.max(0, toInt(offer.price_cents));
  const savings = bundlePrice > 0 && price > 0 ? Math.max(bundlePrice - price, 0) : 0;
  if (bonus > 0) return `+${bonus} BONUS CREDITS`;
  if (savings > 0) return `Save $${(savings / 100).toFixed(2)}`;
  return "Special offer";
};

export const getOfferSavingsCents = (offer: DynamicPricingOffer | null | undefined): number => {
  if (!offer) return 0;
  const bundlePrice = Math.max(0, toInt(offer.bundle_price_cents));
  const price = Math.max(0, toInt(offer.price_cents));
  return bundlePrice > 0 && price > 0 ? Math.max(bundlePrice - price, 0) : 0;
};

export const getOfferBonusCredits = (offer: DynamicPricingOffer | null | undefined): number => {
  if (!offer) return 0;
  return Math.max(0, toInt(offer.bonus_credits));
};

export const getOfferEffectiveCredits = (args: {
  bundleSize: number;
  offer: DynamicPricingOffer | null | undefined;
}): number => {
  const size = Math.max(0, Math.trunc(args.bundleSize));
  const bonus = getOfferBonusCredits(args.offer);
  return size + bonus;
};

export const getOfferUnitPriceText = (args: {
  bundleSize: number;
  offer: DynamicPricingOffer | null | undefined;
  fallbackText: string;
}): string => {
  const size = Math.max(0, Math.trunc(args.bundleSize));
  if (size <= 0) return args.fallbackText;
  const offer = args.offer;
  if (!offer) return args.fallbackText;
  const priceCents = Math.max(0, toInt(offer.price_cents));
  if (priceCents <= 0) return args.fallbackText;
  const effectiveCredits = getOfferEffectiveCredits({ bundleSize: size, offer });
  if (effectiveCredits <= 0) return args.fallbackText;
  const dollarsPerCredit = priceCents / effectiveCredits / 100;
  return `$${dollarsPerCredit.toFixed(2)} ea`;
};

export const pickPreferredOffer = (args: {
  existing: DynamicPricingOffer | null | undefined;
  candidate: DynamicPricingOffer | null | undefined;
  recommendedRedemptionId: string | null;
}): DynamicPricingOffer | null => {
  const { existing, candidate, recommendedRedemptionId } = args;
  if (!existing) return candidate ?? null;
  if (!candidate) return existing;

  const existingRedemptionId = existing.redemption_id ?? null;
  const candidateRedemptionId = candidate.redemption_id ?? null;

  if (recommendedRedemptionId && candidateRedemptionId === recommendedRedemptionId) return candidate;
  if (recommendedRedemptionId && existingRedemptionId === recommendedRedemptionId) return existing;

  const existingBonus = getOfferBonusCredits(existing);
  const candidateBonus = getOfferBonusCredits(candidate);
  if (candidateBonus !== existingBonus) return candidateBonus > existingBonus ? candidate : existing;

  const existingSavings = getOfferSavingsCents(existing);
  const candidateSavings = getOfferSavingsCents(candidate);
  if (candidateSavings !== existingSavings) return candidateSavings > existingSavings ? candidate : existing;

  const existingPrice = Math.max(0, toInt(existing.price_cents));
  const candidatePrice = Math.max(0, toInt(candidate.price_cents));
  if (candidatePrice !== existingPrice) return candidatePrice < existingPrice ? candidate : existing;

  return existing;
};

const readLetterString = (letter: unknown, key: string): string => {
  const l = letter as any;
  if (typeof l?.[key] === "string") return String(l[key]);
  if (typeof l?.data?.[key] === "string") return String(l.data[key]);
  return "";
};

// Identifies the checkout context an offers response was fetched for; any
// change to a segment must trigger a refetch. The e-message facility
// selection is included because the server prices offers by it.
export const offerContextKey = (letter: unknown): string => {
  const l = letter as any;
  const letterId = l?.id ?? l?.data?.id ?? "";
  const emessageFacilityId = l?.eMessage?.id ?? l?.data?.eMessage?.id ?? "";
  const coupon = l?.coupon ?? l?.data?.coupon;
  const rawCouponCode =
    coupon && typeof coupon === "object" && typeof (coupon as any).code === "string"
      ? String((coupon as any).code)
      : "";
  const couponCode = rawCouponCode.trim().toLowerCase();
  return [
    String(letterId),
    readLetterString(letter, "shippingMethod"),
    readLetterString(letter, "messageType"),
    readLetterString(letter, "photoType"),
    couponCode,
    String(emessageFacilityId),
  ].join("|");
};

export const mapOffersByBundle = (resp: LetterOffersResponse | null | undefined): {
  offersByBundle: Record<string, DynamicPricingOffer>;
  badgeTextByBundle: Record<string, string>;
  recommendedRedemptionId: string | null;
} => {
  const offers =
    (resp?.displayed_offers && Array.isArray(resp.displayed_offers) ? resp.displayed_offers : null) ??
    (resp?.recommended_offer ? [resp.recommended_offer] : []) ??
    [];
  const recommendedRedemptionId = resp?.recommended_offer?.redemption_id ?? null;

  const offersByBundle: Record<string, DynamicPricingOffer> = {};
  for (const offer of offers) {
    if (!offer) continue;
    const bundleKey = String(offer.bundle_size ?? "");
    if (!bundleKey) continue;
    const existing = offersByBundle[bundleKey] ?? null;
    const picked = pickPreferredOffer({ existing, candidate: offer, recommendedRedemptionId });
    if (picked) offersByBundle[bundleKey] = picked;
  }

  const badgeTextByBundle: Record<string, string> = {};
  for (const bundleKey of Object.keys(offersByBundle)) {
    const badge = getOfferBadgeText(offersByBundle[bundleKey]);
    if (badge) badgeTextByBundle[bundleKey] = badge;
  }

  return { offersByBundle, badgeTextByBundle, recommendedRedemptionId };
};
